import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'

export interface GamerFeedbackContainerBoxProps extends BoxProps {
  /**
   * Container content
   */
  children: React.ReactNode
  /**
   * Container variant - affects background and styling
   * - team: Gradient background (default)
   * - individual: Heritage blue background
   * - achievements: Heritage blue background (same as individual, for future customization)
   */
  variant?: 'team' | 'individual' | 'achievements'
}

/**
 * GamerFeedbackContainerBox - Main container for feedback components
 *
 * Features:
 * - Two-column layout for team (left) and individual (right) feedback
 * - Consistent background and spacing
 * - Responsive design
 * - Glassmorphism styling for cards
 *
 * @example
 * ```tsx
 * <GamerFeedbackContainerBox isMainWrapper>
 *   <GamerFeedbackContainerBox variant="team">
 *     <GamerFeedbackSummaryContainer />
 *   </GamerFeedbackContainerBox>
 *   <GamerFeedbackContainerBox variant="individual">
 *     <GamerFeedbackSummaryContainer />
 *   </GamerFeedbackContainerBox>
 * </GamerFeedbackContainerBox>
 * ```
 */
export const GamerFeedbackContainerBox: React.FC<GamerFeedbackContainerBoxProps> = ({
  children,
  variant,
  sx,
  ...props
}) => {
  // Conditional background based on variant
  // Keep all colors - 3 variants for future customization
  const background =
    variant === 'individual' || variant === 'achievements'
      ? INTERAX_COLORS.heritageBlue40 // rgba(46, 79, 95, 0.4)
      : 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)'

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        gap: '24px',
        isolation: 'isolate',
        '@media (min-width: 1850px)': {
          width: '100%'
        },
        // Flex properties from design spec
        flex: 'none',
        alignSelf: 'stretch',
        flexGrow: 1,

        // Background varies by variant
        background,
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
        borderRadius: '10px',

        minWidth: 0, // Prevents flex item from overflowing

        // Custom styles
        ...sx
      }}
      {...props}
    >
      {children}
    </Box>
  )
}

export default GamerFeedbackContainerBox
